'use client';

import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { SignInButton, useUser } from '@clerk/nextjs';
import { useRouter } from 'next/navigation';
import { cn } from '@/lib/cn';

export function HeroSection() {
  const { isSignedIn } = useUser();
  const router = useRouter();

  return (
    <section className="relative overflow-hidden py-20 sm:py-32">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text Content */}
          <div className="max-w-xl">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className={cn(
                "inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium",
                "border border-[var(--color-border)]",
                "bg-[var(--color-surface)]"
              )} style={{ color: 'var(--color-accent-muted)' }}>
                <Sparkles size={14} />
                AI-Powered Portfolio Tracking
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[var(--color-text)]"
            >
              Track Your Portfolio{' '}
              <span style={{ color: 'var(--color-accent-muted)' }}>Like a Pro</span>
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-6 text-lg leading-relaxed"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Visualize your portfolio with interactive treemaps, track individual lots,
              monitor ETF allocations, and get AI-powered research — all in one beautiful dashboard.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-8 flex flex-wrap gap-4"
            >
              {isSignedIn ? (
                <button
                  onClick={() => router.push('/')}
                  className={cn(
                    "px-6 py-3 rounded-xl text-sm font-medium transition-colors",
                    "bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]"
                  )}
                >
                  Go to Dashboard
                </button>
              ) : (
                <SignInButton mode="modal">
                  <button className={cn(
                    "px-6 py-3 rounded-xl text-sm font-medium transition-colors",
                    "bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]"
                  )}>
                    Get Started Free
                  </button>
                </SignInButton>
              )}
              <a
                href="#features"
                className={cn(
                  "px-6 py-3 rounded-xl text-sm font-medium transition-colors",
                  "border border-[var(--color-border)] bg-transparent",
                  "hover:bg-[var(--color-surface-secondary)]"
                )}
                style={{ color: 'var(--color-text-secondary)' }}
              >
                See Features
              </a>
            </motion.div>
          </div>

          {/* Right: Dashboard Mockup */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className=""
          >
            <div className="animate-float rounded-2xl p-6 border border-[var(--color-border-subtle)]" style={{ backgroundColor: 'var(--color-surface)' }}>
              {/* Mockup Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500/60" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                  <div className="w-3 h-3 rounded-full bg-green-500/60" />
                </div>
                <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Portfolio Dashboard</span>
              </div>

              {/* Mini Summary Strip */}
              <div className="grid grid-cols-3 gap-3 mb-4 p-3 rounded-xl" style={{ backgroundColor: 'var(--color-bg)' }}>
                <div>
                  <p className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>Value</p>
                  <p className="text-sm font-bold text-[var(--color-text)]">$42,730</p>
                </div>
                <div>
                  <p className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>Cost</p>
                  <p className="text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>$35,200</p>
                </div>
                <div>
                  <p className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>P/L</p>
                  <p className="text-sm font-bold text-green-400">+$7,530</p>
                </div>
              </div>

              {/* Portfolio Value Graph Mockup */}
              <div className="mb-4 rounded-lg p-3" style={{ backgroundColor: 'var(--color-bg)' }}>
                <div className="flex items-baseline justify-between mb-2">
                  <div>
                    <p className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>Portfolio Value</p>
                    <p className="text-sm font-bold text-[var(--color-text)]">$42,730</p>
                  </div>
                  <span className="text-[10px] font-medium text-green-400">+21.4% all time</span>
                </div>
                <svg viewBox="0 0 280 80" className="w-full h-20" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="graphFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--color-accent)" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="var(--color-accent)" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M0,65 L20,60 L40,55 L60,50 L80,52 L100,40 L120,42 L140,35 L160,30 L180,32 L200,22 L220,18 L240,20 L260,12 L280,10"
                    fill="none"
                    stroke="var(--color-accent)"
                    strokeWidth="2"
                  />
                  <path
                    d="M0,65 L20,60 L40,55 L60,50 L80,52 L100,40 L120,42 L140,35 L160,30 L180,32 L200,22 L220,18 L240,20 L260,12 L280,10 L280,80 L0,80 Z"
                    fill="url(#graphFill)"
                  />
                </svg>
                <div className="flex justify-between mt-1">
                  {['Jan', 'Mar', 'May', 'Jul', 'Sep', 'Nov'].map((m) => (
                    <span key={m} className="text-[8px]" style={{ color: 'var(--color-text-muted)' }}>{m}</span>
                  ))}
                </div>
              </div>

              {/* Your Assets - Stock Cards */}
              <p className="text-xs font-bold text-[var(--color-text)] mb-2">Your Assets</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { ticker: 'AAPL', exchange: 'NAS', name: 'APPLE INC', price: '$234.00', change: '+30.0%', up: true, shares: '80.00', profit: '+$4,320' },
                  { ticker: 'MSFT', exchange: 'NAS', name: 'MICROSOFT CORP', price: '$418.00', change: '+22.9%', up: true, shares: '25.00', profit: '+$1,950' },
                  { ticker: 'NVDA', exchange: 'NAS', name: 'NVIDIA CORP', price: '$140.00', change: '+40.0%', up: true, shares: '60.00', profit: '+$2,400' },
                  { ticker: 'TSLA', exchange: 'NAS', name: 'TESLA INC', price: '$172.00', change: '-18.1%', up: false, shares: '30.00', profit: '-$1,140' },
                ].map((stock) => (
                  <div key={stock.ticker} className="rounded-lg p-2.5" style={{ backgroundColor: 'var(--color-bg)' }}>
                    <div className="flex items-start justify-between mb-1.5">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] font-bold text-[var(--color-text)]">{stock.ticker}</span>
                          <span className="text-[8px] px-1 py-0.5 rounded" style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text-primary)' }}>{stock.exchange}</span>
                        </div>
                        <p className="text-[8px] truncate" style={{ color: 'var(--color-text-secondary)' }}>{stock.name}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-[10px] font-bold text-[var(--color-text)]">{stock.price}</p>
                        <p className={cn("text-[8px] font-medium", stock.up ? 'text-green-400' : 'text-red-400')}>{stock.change}</p>
                      </div>
                    </div>
                    <div className="flex justify-between pt-1.5" style={{ borderTop: '1px solid var(--color-border-subtle)' }}>
                      <div>
                        <p className="text-[7px]" style={{ color: 'var(--color-text-muted)' }}>Your Shares</p>
                        <p className="text-[9px] font-semibold" style={{ color: 'var(--color-text-primary)' }}>{stock.shares}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[7px]" style={{ color: 'var(--color-text-muted)' }}>Total Profit (USD)</p>
                        <p className={cn("text-[9px] font-semibold", stock.up ? 'text-green-400' : 'text-red-400')}>{stock.profit}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
